import { PathMatch, useMatch, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  CompanyCapitalType,
  FundraisingType,
  useGetCompanyQuery,
} from '@blockpulse3/graphql/hooks';

type IFundraisingRoutes = typeof routes.workspace.fundraising;
type UseFundraisingSteps = {
  /* ** Current step number ** */
  progress: number;
  /* ** Available steps ** */
  activeSteps: {
    label: string;
    value: PathMatch<string> | null;
  }[];
};

/**
 * useFundraisingSteps.
 * Build available steps depending on the company capital type and the type of the fundraising.
 * `activeSteps` looks for the first PathMatch, that is the current step from the URL.
 *
 * @param {IRoutes['private'] | IRoutes['crowdfunding']} fundraisingRoutes
 * @param {FundraisingType} type
 * @returns {UseFundraisingSteps}
 */
export function useFundraisingSteps(
  fundraisingRoutes: IFundraisingRoutes['private'] | IFundraisingRoutes['crowdfunding'],
  type: FundraisingType,
): UseFundraisingSteps {
  const i18nFundraisingLayout = useTranslations('NewFundraisingLayout');

  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const isPrivate = type === FundraisingType.PRIVATE;
  const isFixedCapital = company?.capitalType === CompanyCapitalType.FIXED;

  const isCreateActive = useMatch(fundraisingRoutes.create.full);
  const isParametersActive = useMatch(fundraisingRoutes.parameters.full);
  const isDocumentationActive = useMatch(fundraisingRoutes.documentation.full);
  const isIBANActive = useMatch(fundraisingRoutes.iban.full);
  const isSubscribersActive = useMatch(routes.workspace.fundraising.private.subscribers.full);
  const isSummaryActive = useMatch(fundraisingRoutes.summary.full);

  const activeSteps = [
    {
      label: i18nFundraisingLayout('NewFundraisingParameters'),
      value: isParametersActive || isCreateActive,
    },
    { label: i18nFundraisingLayout('NewFundraisingDocumentation'), value: isDocumentationActive },
    ...(isFixedCapital
      ? [{ label: i18nFundraisingLayout('NewFundraisingIBAN'), value: isIBANActive }]
      : []),
    ...(isPrivate
      ? [{ label: i18nFundraisingLayout('NewFundraisingSubscribers'), value: isSubscribersActive }]
      : []),
    { label: i18nFundraisingLayout('NewFundraisingSummary'), value: isSummaryActive },
  ];

  const progress = activeSteps.findIndex((v) => v.value);

  return { progress, activeSteps };
}
